import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Calendar as CalendarComponent } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Pencil, Trash2, Lock, Unlock, Search, UserPlus, Users, Eye, EyeOff, Plus, X, CreditCard, BookOpen, Calendar, CalendarIcon, Shield, UserCog, GraduationCap, ClipboardCheck } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_TABS = [
  { id: "all", label: "Alle", icon: Users },
  { id: "participant", label: "Teilnehmer", icon: Users },
  { id: "admin", label: "Administratoren", icon: UserCog },
  { id: "dozent", label: "Dozenten", icon: GraduationCap },
  { id: "auditor", label: "ISO-Auditoren", icon: ClipboardCheck },
];

const ROLE_LABELS = {
  participant: "Teilnehmer",
  admin: "Administrator",
  super_admin: "Super-Admin",
  dozent: "Dozent",
  auditor: "ISO-Auditor",
};

const ROLE_COLORS = {
  participant: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  admin: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
  super_admin: "bg-red-500/10 text-red-500 border-red-500/30",
  dozent: "bg-green-500/10 text-green-500 border-green-500/30",
  auditor: "bg-purple-500/10 text-purple-500 border-purple-500/30",
};

// Alle Buchungstypen
const BOOKING_TYPES = [
  { id: "bq_abo", label: "BQ-Abonnement", category: "abo", icon: CreditCard },
  { id: "hq_abo", label: "HQ-Abonnement", category: "abo", icon: CreditCard, hasSubType: true },
  { id: "bq_intensiv", label: "BQ-Intensivkurs", category: "intensiv", icon: BookOpen },
  { id: "hq_intensiv", label: "HQ-Intensivkurs", category: "intensiv", icon: BookOpen },
  { id: "probetraining", label: "Probetraining", category: "other", icon: Calendar },
  { id: "mathe_grundkurs", label: "Mathe-Grundkurs", category: "other", icon: BookOpen },
  { id: "fachgespraech", label: "Fachgespräch", category: "other", icon: Users },
];

const HQ_SUB_TYPES = [
  { id: "metall", label: "HQ Abo Metalltechnik" },
];

const BOOKING_TYPE_LABELS = {
  bq_abo: "BQ-Abo",
  hq_abo: "HQ-Abo",
  bq_intensiv: "BQ-Intensiv",
  hq_intensiv: "HQ-Intensiv",
  probetraining: "Probe",
  mathe_grundkurs: "Mathe",
  fachgespraech: "Fachgespräch"
};

const BookingBadge = ({ booking, onDelete }) => {
  const label = BOOKING_TYPE_LABELS[booking.booking_type] || booking.booking_type;
  const subLabel = booking.hq_sub_type ? ` (${booking.hq_sub_type})` : "";
  const colorMap = {
    bq_abo: "bg-primary/15 text-primary border-primary/50",
    hq_abo: "bg-blue-500/15 text-blue-500 border-blue-500/50",
    bq_intensiv: "bg-green-500/15 text-green-500 border-green-500/50",
    hq_intensiv: "bg-purple-500/15 text-purple-500 border-purple-500/50",
    probetraining: "bg-orange-500/15 text-orange-500 border-orange-500/50",
    mathe_grundkurs: "bg-pink-500/15 text-pink-500 border-pink-500/50",
    fachgespraech: "bg-teal-500/15 text-teal-500 border-teal-500/50"
  };
  
  return (
    <Badge className={`${colorMap[booking.booking_type] || "bg-secondary"} rounded-sm text-xs font-medium border flex items-center gap-1`}>
      {label}{subLabel}
      {onDelete && (
        <button 
          onClick={(e) => { e.stopPropagation(); onDelete(booking.id); }}
          className="ml-1 hover:text-destructive"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </Badge>
  );
};

const Participants = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const isDozent = currentUser?.role === "dozent";
  const canSeeEmail = !isDozent; // Dozenten see only names
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeRole, setActiveRole] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingsDialogOpen, setBookingsDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [managingUser, setManagingUser] = useState(null);
  const [roleChangeUser, setRoleChangeUser] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form für Teilnehmer erstellen/bearbeiten
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    industriemeister_fachrichtung: ""
  });

  // Buchungen für neuen Teilnehmer
  const [newBookings, setNewBookings] = useState([]);
  
  // Neue Buchung Form
  const [newBookingForm, setNewBookingForm] = useState({
    booking_type: "",
    hq_sub_type: "",
    duration_months: 1,
    start_date: format(new Date(), "yyyy-MM-dd"),
    probetraining_subject: "",
    probetraining_date: ""
  });

  useEffect(() => {
    fetchParticipants();
  }, []);

  const fetchParticipants = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setParticipants(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Teilnehmer");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({ 
      name: "", 
      email: "", 
      phone: "", 
      password: "",
      industriemeister_fachrichtung: ""
    });
    setNewBookings([]);
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || "",
      password: "",
      industriemeister_fachrichtung: user.industriemeister_fachrichtung || ""
    });
    setNewBookings([]);
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleOpenBookings = (user) => {
    setManagingUser(user);
    setNewBookingForm({
      booking_type: "",
      hq_sub_type: "",
      duration_months: 1,
      start_date: format(new Date(), "yyyy-MM-dd"),
      probetraining_subject: "",
      probetraining_date: ""
    });
    setBookingsDialogOpen(true);
  };

  // Buchung zur Liste hinzufügen (für neuen Teilnehmer)
  const addBookingToList = () => {
    if (!newBookingForm.booking_type) {
      toast.error("Bitte Buchungstyp auswählen");
      return;
    }
    
    if (newBookingForm.booking_type === "hq_abo" && !newBookingForm.hq_sub_type) {
      toast.error("Bitte HQ-Fachrichtung auswählen");
      return;
    }
    
    if (newBookingForm.booking_type === "probetraining" && (!newBookingForm.probetraining_subject || !newBookingForm.probetraining_date)) {
      toast.error("Bitte Fach und Datum für Probetraining angeben");
      return;
    }

    const booking = {
      id: `temp-${Date.now()}`,
      booking_type: newBookingForm.booking_type,
      hq_sub_type: newBookingForm.booking_type === "hq_abo" ? newBookingForm.hq_sub_type : null,
      duration_months: ["bq_abo", "hq_abo"].includes(newBookingForm.booking_type) ? newBookingForm.duration_months : null,
      start_date: newBookingForm.start_date,
      probetraining_subject: newBookingForm.booking_type === "probetraining" ? newBookingForm.probetraining_subject : null,
      probetraining_date: newBookingForm.booking_type === "probetraining" ? newBookingForm.probetraining_date : null,
    };
    
    setNewBookings([...newBookings, booking]);
    setNewBookingForm({
      booking_type: "",
      hq_sub_type: "",
      duration_months: 1,
      start_date: format(new Date(), "yyyy-MM-dd"),
      probetraining_subject: "",
      probetraining_date: ""
    });
    toast.success("Buchung hinzugefügt");
  };

  const removeBookingFromList = (bookingId) => {
    setNewBookings(newBookings.filter(b => b.id !== bookingId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (editingUser) {
        // Update existing user
        const updateData = { 
          name: formData.name, 
          phone: formData.phone, 
          email: formData.email,
          industriemeister_fachrichtung: formData.industriemeister_fachrichtung
        };
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success("Teilnehmer aktualisiert");
      } else {
        // Create new user
        const userResponse = await axios.post(`${API}/users`, { 
          ...formData, 
          role: "participant" 
        });
        
        // Create bookings for new user
        if (newBookings.length > 0) {
          for (const booking of newBookings) {
            await axios.post(`${API}/bookings`, {
              user_id: userResponse.data.id,
              booking_type: booking.booking_type,
              hq_sub_type: booking.hq_sub_type,
              duration_months: booking.duration_months,
              start_date: booking.start_date,
              probetraining_subject: booking.probetraining_subject,
              probetraining_date: booking.probetraining_date
            });
          }
        }
        toast.success(`Teilnehmer erstellt${newBookings.length > 0 ? ` mit ${newBookings.length} Buchung(en)` : ""}`);
      }
      setDialogOpen(false);
      fetchParticipants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  };

  // Buchung für bestehenden Teilnehmer hinzufügen
  const handleAddBookingToUser = async () => {
    if (!newBookingForm.booking_type) {
      toast.error("Bitte Buchungstyp auswählen");
      return;
    }
    
    if (newBookingForm.booking_type === "hq_abo" && !newBookingForm.hq_sub_type) {
      toast.error("Bitte HQ-Fachrichtung auswählen");
      return;
    }
    
    if (newBookingForm.booking_type === "probetraining" && (!newBookingForm.probetraining_subject || !newBookingForm.probetraining_date)) {
      toast.error("Bitte Fach und Datum für Probetraining angeben");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        user_id: managingUser.id,
        booking_type: newBookingForm.booking_type,
        hq_sub_type: newBookingForm.booking_type === "hq_abo" ? newBookingForm.hq_sub_type : null,
        duration_months: newBookingForm.booking_type !== "probetraining" ? (newBookingForm.duration_months || 1) : null,
        start_date: newBookingForm.start_date,
        probetraining_subject: newBookingForm.booking_type === "probetraining" ? newBookingForm.probetraining_subject : null,
        probetraining_date: newBookingForm.booking_type === "probetraining" ? newBookingForm.probetraining_date : null
      });
      toast.success("Buchung hinzugefügt");
      
      // Refresh user data
      const response = await axios.get(`${API}/users`);
      setParticipants(response.data);
      const updatedUser = response.data.find(u => u.id === managingUser.id);
      if (updatedUser) setManagingUser(updatedUser);
      
      setNewBookingForm({
        booking_type: "",
        hq_sub_type: "",
        duration_months: 1,
        start_date: format(new Date(), "yyyy-MM-dd"),
        probetraining_subject: "",
        probetraining_date: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen");
    } finally {
      setSubmitting(false);
    }
  };

  // Buchung löschen
  const handleDeleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      toast.success("Buchung gelöscht");
      
      // Refresh user data
      const response = await axios.get(`${API}/users`);
      setParticipants(response.data);
      const updatedUser = response.data.find(u => u.id === managingUser.id);
      if (updatedUser) setManagingUser(updatedUser);
    } catch (error) {
      toast.error("Fehler beim Löschen der Buchung");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${deletingUser.id}`);
      toast.success("Teilnehmer und alle Buchungen gelöscht");
      setDeleteDialogOpen(false);
      setDeletingUser(null);
      fetchParticipants();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleToggleLock = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, { is_locked: !user.is_locked });
      toast.success(user.is_locked ? "Zugang freigeschaltet" : "Zugang gesperrt");
      fetchParticipants();
    } catch (error) {
      toast.error("Fehler beim Ändern des Status");
    }
  };

  const handleOpenRoleChange = (user) => {
    setRoleChangeUser(user);
    setNewRole(user.role);
    setRoleDialogOpen(true);
  };

  const handleRoleChange = async () => {
    if (!roleChangeUser || !newRole) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/users/${roleChangeUser.id}/role?new_role=${newRole}`);
      toast.success(`Rolle geändert: ${roleChangeUser.name} ist jetzt ${ROLE_LABELS[newRole]}`);
      setRoleDialogOpen(false);
      setRoleChangeUser(null);
      fetchParticipants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Ändern der Rolle");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredParticipants = participants.filter(p => {
    const matchRole = activeRole === "all" || p.role === activeRole;
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.email.toLowerCase().includes(search.toLowerCase()) ||
      (p.phone && p.phone.includes(search));
    return matchRole && matchSearch;
  });

  // Buchungstyp-Formular Komponente
  const BookingTypeForm = ({ form, setForm, isForNewUser = false }) => {
    const selectedType = BOOKING_TYPES.find(t => t.id === form.booking_type);
    const isAbo = ["bq_abo", "hq_abo"].includes(form.booking_type);
    const isProbetraining = form.booking_type === "probetraining";
    const needsHqSubType = form.booking_type === "hq_abo";

    return (
      <div className="space-y-4 p-4 bg-secondary/20 rounded-sm border border-border">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Buchungstyp *</Label>
            <Select
              value={form.booking_type}
              onValueChange={(value) => setForm({ ...form, booking_type: value, hq_sub_type: "" })}
            >
              <SelectTrigger className="bg-background border-input" data-testid="booking-type-select">
                <SelectValue placeholder="Typ auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bq_abo">BQ-Abonnement</SelectItem>
                <SelectItem value="hq_abo">HQ-Abonnement</SelectItem>
                <SelectItem value="bq_intensiv">BQ-Intensivkurs</SelectItem>
                <SelectItem value="hq_intensiv">HQ-Intensivkurs</SelectItem>
                <SelectItem value="probetraining">Probetraining</SelectItem>
                <SelectItem value="mathe_grundkurs">Mathe-Grundkurs</SelectItem>
                <SelectItem value="fachgespraech">Fachgespräch</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {needsHqSubType && (
            <div className="space-y-2">
              <Label>HQ-Fachrichtung *</Label>
              <Select
                value={form.hq_sub_type}
                onValueChange={(value) => setForm({ ...form, hq_sub_type: value })}
              >
                <SelectTrigger className="bg-background border-input" data-testid="hq-subtype-select">
                  <SelectValue placeholder="Fachrichtung wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="metall">HQ Abo Metalltechnik</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {isAbo && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Startdatum</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-background border-input"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.start_date ? (
                      format(new Date(form.start_date), "dd.MM.yyyy", { locale: de })
                    ) : (
                      "Datum wählen"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={form.start_date ? new Date(form.start_date) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        setForm({ ...form, start_date: format(date, "yyyy-MM-dd") });
                      }
                    }}
                    locale={de}
                    className="rounded-md"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Laufzeit</Label>
              <Select
                value={String(form.duration_months)}
                onValueChange={(value) => setForm({ ...form, duration_months: parseInt(value) })}
              >
                <SelectTrigger className="bg-background border-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <SelectItem key={m} value={String(m)}>{m} {m === 1 ? "Monat" : "Monate"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {isProbetraining && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fach *</Label>
              <Input
                value={form.probetraining_subject}
                onChange={(e) => setForm({ ...form, probetraining_subject: e.target.value })}
                placeholder="z.B. NTG, BWH..."
                className="bg-background border-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Datum *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-background border-input"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.probetraining_date ? (
                      format(new Date(form.probetraining_date), "dd.MM.yyyy", { locale: de })
                    ) : (
                      "Datum wählen"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={form.probetraining_date ? new Date(form.probetraining_date) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        setForm({ ...form, probetraining_date: format(date, "yyyy-MM-dd") });
                      }
                    }}
                    locale={de}
                    className="rounded-md"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}

        <Button 
          type="button" 
          onClick={isForNewUser ? addBookingToList : handleAddBookingToUser}
          disabled={!form.booking_type || submitting}
          className="w-full gap-2"
          data-testid="add-booking-btn"
        >
          <Plus className="h-4 w-4" />
          {submitting ? "Wird hinzugefügt..." : "Buchung hinzufügen"}
        </Button>
      </div>
    );
  };

  // Count stats
  const totalCount = participants.length;
  const activeCount = participants.filter(p => !p.is_locked).length;
  const lockedCount = participants.filter(p => p.is_locked).length;

  return (
    <AdminLayout title="Benutzerverwaltung">
      <div className="space-y-6" data-testid="participants-page">
        {/* Role Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="role-tabs">
          {ROLE_TABS.map(tab => {
            const Icon = tab.icon;
            const count = tab.id === "all" ? participants.length : participants.filter(p => p.role === tab.id).length;
            const isActive = activeRole === tab.id;
            return (
              <Button
                key={tab.id}
                variant={isActive ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveRole(tab.id)}
                className="gap-2"
                data-testid={`role-tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label} ({count})
              </Button>
            );
          })}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Gesamt</p>
                  <p className="text-2xl font-bold">{totalCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-green-500/10 flex items-center justify-center">
                  <Unlock className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Aktiv</p>
                  <p className="text-2xl font-bold text-green-500">{activeCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-red-500/10 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Gesperrt</p>
                  <p className="text-2xl font-bold text-red-500">{lockedCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={isSuperAdmin ? "Suche nach Name, E-Mail oder Telefon..." : (isDozent ? "Suche nach Name..." : "Suche nach Name oder E-Mail...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-card border-border"
              data-testid="search-input"
            />
          </div>
          <Button onClick={handleOpenCreate} className="gap-2" data-testid="create-participant-btn">
            <UserPlus className="h-4 w-4" />
            Neuer Teilnehmer
          </Button>
        </div>

        {/* Participants Table */}
        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Teilnehmerliste ({filteredParticipants.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredParticipants.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Keine Teilnehmer gefunden</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Name</TableHead>
                    {canSeeEmail && (
                      <TableHead className="text-muted-foreground">E-Mail</TableHead>
                    )}
                    <TableHead className="text-muted-foreground">Rolle</TableHead>
                    {isSuperAdmin && (
                      <TableHead className="text-muted-foreground">Telefon</TableHead>
                    )}
                    <TableHead className="text-muted-foreground">Buchungen</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredParticipants.map((user) => {
                    const activeBookings = (user.bookings || []).filter(b => b.access_allowed);
                    const hasExpiring = activeBookings.some(b => b.status === "expiring_soon");
                    const hasExpired = (user.bookings || []).some(b => b.status === "expired");
                    
                    return (
                      <TableRow key={user.id} className="border-border" data-testid={`participant-row-${user.id}`}>
                        <TableCell className="font-medium">
                          {user.name}
                          {user.industriemeister_fachrichtung && (
                            <p className="text-xs text-muted-foreground">{user.industriemeister_fachrichtung}</p>
                          )}
                        </TableCell>
                        {canSeeEmail && (
                          <TableCell className="text-muted-foreground font-mono text-sm">{user.email}</TableCell>
                        )}
                        <TableCell>
                          <Badge className={`${ROLE_COLORS[user.role] || ROLE_COLORS.participant} border cursor-pointer`} onClick={() => handleOpenRoleChange(user)} data-testid={`role-badge-${user.id}`}>
                            {ROLE_LABELS[user.role] || user.role}
                          </Badge>
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell className="text-muted-foreground">{user.phone || "-"}</TableCell>
                        )}
                        <TableCell>
                          <div 
                            className="flex flex-wrap gap-1 cursor-pointer hover:opacity-80" 
                            onClick={() => handleOpenBookings(user)}
                            title="Klicken zum Verwalten"
                          >
                            {user.bookings && user.bookings.length > 0 ? (
                              <>
                                {user.bookings.slice(0, 3).map((booking, idx) => (
                                  <BookingBadge key={idx} booking={booking} />
                                ))}
                                {user.bookings.length > 3 && (
                                  <Badge className="bg-secondary rounded-sm text-xs">
                                    +{user.bookings.length - 3}
                                  </Badge>
                                )}
                              </>
                            ) : (
                              <Badge variant="outline" className="rounded-sm">Keine</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.is_locked ? (
                            <Badge className="status-expired rounded-sm">Gesperrt</Badge>
                          ) : hasExpiring ? (
                            <Badge className="status-warning rounded-sm">Läuft ab</Badge>
                          ) : hasExpired && activeBookings.length === 0 ? (
                            <Badge className="status-expired rounded-sm">Abgelaufen</Badge>
                          ) : activeBookings.length > 0 ? (
                            <Badge className="status-active rounded-sm">Aktiv</Badge>
                          ) : (
                            <Badge variant="outline" className="rounded-sm">Inaktiv</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {user.role !== "super_admin" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenRoleChange(user)}
                                title="Rolle ändern"
                                data-testid={`change-role-${user.id}`}
                              >
                                <Shield className="h-4 w-4 text-purple-500" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenBookings(user)}
                              title="Buchungen verwalten"
                              data-testid={`manage-bookings-${user.id}`}
                            >
                              <CreditCard className="h-4 w-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleLock(user)}
                              title={user.is_locked ? "Zugang aktivieren" : "Zugang sperren"}
                              data-testid={`toggle-lock-${user.id}`}
                            >
                              {user.is_locked ? (
                                <Unlock className="h-4 w-4 text-green-500" />
                              ) : (
                                <Lock className="h-4 w-4 text-yellow-500" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenEdit(user)}
                              title="Bearbeiten"
                              data-testid={`edit-${user.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setDeletingUser(user); setDeleteDialogOpen(true); }}
                              title="Löschen"
                              data-testid={`delete-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit User Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="participant-dialog">
            <DialogHeader>
              <DialogTitle>{editingUser ? "Teilnehmer bearbeiten" : "Neuer Teilnehmer"}</DialogTitle>
              <DialogDescription>
                {editingUser 
                  ? "Aktualisieren Sie die Daten des Teilnehmers." 
                  : "Erstellen Sie einen neuen Teilnehmer mit Buchungen."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Personal Data */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Max Mustermann"
                    required
                    className="bg-background border-input"
                    data-testid="name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="max@beispiel.de"
                    required
                    className="bg-background border-input"
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {isSuperAdmin && (
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+49 123 456789"
                      className="bg-background border-input"
                      data-testid="phone-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="fachrichtung">Industriemeister-Fachrichtung</Label>
                  <Input
                    id="fachrichtung"
                    value={formData.industriemeister_fachrichtung}
                    onChange={(e) => setFormData({ ...formData, industriemeister_fachrichtung: e.target.value })}
                    placeholder="z.B. Metall, Elektrotechnik..."
                    className="bg-background border-input"
                    data-testid="fachrichtung-input"
                  />
                </div>
              </div>
              
              {!editingUser && (
                <div className="space-y-2">
                  <Label htmlFor="password">Passwort *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Passwort"
                      required
                      className="bg-background border-input pr-10"
                      data-testid="password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}

              {/* Bookings Section for New User */}
              {!editingUser && (
                <div className="space-y-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Buchungen hinzufügen</Label>
                    {newBookings.length > 0 && (
                      <Badge className="bg-primary/15 text-primary">{newBookings.length} Buchung(en)</Badge>
                    )}
                  </div>
                  
                  {/* Added Bookings */}
                  {newBookings.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-secondary/20 rounded-sm">
                      {newBookings.map((booking) => (
                        <BookingBadge 
                          key={booking.id} 
                          booking={booking} 
                          onDelete={() => removeBookingFromList(booking.id)}
                        />
                      ))}
                    </div>
                  )}
                  
                  {/* Booking Form */}
                  <BookingTypeForm 
                    form={newBookingForm} 
                    setForm={setNewBookingForm} 
                    isForNewUser={true}
                  />
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-participant-btn">
                  {submitting ? "Speichert..." : (editingUser ? "Aktualisieren" : "Erstellen")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Manage Bookings Dialog */}
        <Dialog open={bookingsDialogOpen} onOpenChange={setBookingsDialogOpen}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bookings-dialog">
            <DialogHeader>
              <DialogTitle>Buchungen verwalten</DialogTitle>
              <DialogDescription>
                {managingUser?.name} ({managingUser?.email})
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Current Bookings */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Aktuelle Buchungen</Label>
                {managingUser?.bookings && managingUser.bookings.length > 0 ? (
                  <div className="space-y-2">
                    {managingUser.bookings.map((booking) => (
                      <div 
                        key={booking.id} 
                        className="flex items-center justify-between p-3 bg-secondary/20 rounded-sm border border-border"
                      >
                        <div className="flex items-center gap-3">
                          <BookingBadge booking={booking} />
                          <div className="text-sm">
                            {booking.start_date && (
                              <span className="text-muted-foreground">
                                {format(new Date(booking.start_date), "dd.MM.yyyy")}
                                {booking.end_date && ` - ${format(new Date(booking.end_date), "dd.MM.yyyy")}`}
                              </span>
                            )}
                            {booking.duration_months && (
                              <span className="text-muted-foreground ml-2">
                                ({booking.duration_months} Mon.)
                              </span>
                            )}
                          </div>
                          <Badge 
                            className={`rounded-sm text-xs ${
                              booking.status === "active" ? "status-active" :
                              booking.status === "expiring_soon" ? "status-warning" :
                              "status-expired"
                            }`}
                          >
                            {booking.status === "active" ? "Aktiv" : 
                             booking.status === "expiring_soon" ? "Läuft ab" : "Abgelaufen"}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteBooking(booking.id)}
                          title="Buchung löschen"
                          data-testid={`delete-booking-${booking.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground p-3 bg-secondary/20 rounded-sm">
                    Keine Buchungen vorhanden
                  </p>
                )}
              </div>

              {/* Add New Booking */}
              <div className="space-y-3 pt-4 border-t border-border">
                <Label className="text-base font-semibold">Neue Buchung hinzufügen</Label>
                <BookingTypeForm 
                  form={newBookingForm} 
                  setForm={setNewBookingForm} 
                  isForNewUser={false}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setBookingsDialogOpen(false)}>
                Schließen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Role Change Dialog */}
        <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
          <DialogContent className="bg-card border-border max-w-sm" data-testid="role-change-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Rolle zuweisen
              </DialogTitle>
              <DialogDescription>
                {roleChangeUser?.name} ({roleChangeUser?.email})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {[
                { id: "participant", label: "Teilnehmer", desc: "Zugriff auf Lernplattform und Kurse", icon: Users },
                { id: "admin", label: "Administrator", desc: "Verwaltungszugriff auf das Portal", icon: UserCog },
                { id: "dozent", label: "Dozent", desc: "Zugriff auf Kurstools und Teilnehmerlisten", icon: GraduationCap },
                { id: "auditor", label: "ISO-Auditor", desc: "Lesezugriff auf das QM-System", icon: ClipboardCheck },
              ].map(role => {
                const Icon = role.icon;
                const selected = newRole === role.id;
                return (
                  <div
                    key={role.id}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                      selected ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/30"
                    }`}
                    onClick={() => setNewRole(role.id)}
                    data-testid={`role-option-${role.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selected ? "bg-primary/10" : "bg-secondary"}`}>
                      <Icon className={`h-5 w-5 ${selected ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${selected ? "text-primary" : ""}`}>{role.label}</p>
                      <p className="text-xs text-muted-foreground">{role.desc}</p>
                    </div>
                    {selected && <div className="w-3 h-3 rounded-full bg-primary" />}
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRoleDialogOpen(false)}>Abbrechen</Button>
              <Button onClick={handleRoleChange} disabled={submitting || newRole === roleChangeUser?.role} data-testid="confirm-role-change">
                {submitting ? "Speichert..." : "Rolle zuweisen"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-card border-border" data-testid="delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Teilnehmer löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie &ldquo;{deletingUser?.name}&rdquo; wirklich löschen? 
                <br /><br />
                <strong className="text-destructive">ACHTUNG:</strong> Dies löscht auch alle Buchungen und Nachrichten dieses Teilnehmers. Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete-btn"
              >
                Endgültig löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default Participants;
